import React from 'react';
import { ArrowRight, Wrench, Zap, ShieldCheck, DoorOpen, Cable } from 'lucide-react';
import { SERVICES, BUSINESS } from '@/lib/businessData';
import { Image } from '@/components/ui/image';

const ICONS = {
  spring: Wrench,
  motor: Zap,
  sensor: ShieldCheck,
  cable: Cable,
  panel: DoorOpen,
};

export default function ServiceMatrix() {
  return (
    <section id="services" className="relative py-20 lg:py-28 bg-background border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono text-xs text-primary uppercase tracking-wider stencil">/ 02 — Service Matrix</span>
            <div className="slat-divider flex-1" />
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-foreground text-balance">
            Every Garage Door Repair Service in Cypress, TX
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            From broken torsion springs to smart opener installation — Mike's handles the full spectrum of
            residential garage door repair across Cypress and surrounding Harris County communities.
          </p>
        </div>

        {/* Service cards — horizontal slats */}
        <div className="space-y-4">
          {SERVICES.map((service, idx) => {
            const Icon = ICONS[service.id] || Wrench;
            return (
              <article
                key={service.id}
                className="group relative bg-card border border-border overflow-hidden transition-all duration-300 hover:border-primary/50"
              >
                <div className="grid sm:grid-cols-[200px_1fr_auto] items-stretch">
                  {/* Image */}
                  <div className="relative h-40 sm:h-full overflow-hidden bg-background">
                    <Image
                      src={service.image}
                      alt={`${service.title} — ${service.keyword}`}
                      fittingType="fill"
                      className="block w-full h-full opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent sm:bg-gradient-to-r" />
                  </div>

                  {/* Content */}
                  <div className="p-6 lg:p-8 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-xs text-muted-foreground">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <Icon className="w-4 h-4 text-primary" />
                      <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
                        {service.short}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-xl lg:text-2xl text-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-2xl">
                      {service.desc}
                    </p>
                    <span className="font-mono text-[10px] text-muted-foreground mt-3 hidden lg:block">
                      Search: {service.keyword}
                    </span>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center justify-center gap-3 p-6 sm:p-8 sm:border-l border-border bg-background/30">
                    <a
                      href="#booking"
                      className="flex items-center gap-1 font-heading font-bold text-xs uppercase tracking-wider text-foreground hover:text-primary transition-colors whitespace-nowrap"
                      aria-label={`Book ${service.title}`}
                    >
                      Book
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 bg-muted border border-border p-6">
          <p className="font-mono text-sm text-muted-foreground text-center sm:text-left">
            Don't see your issue? We handle <strong className="text-foreground">everything garage door</strong> — remotes, keypads, weather seals, drums, hinges, and more.
          </p>
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            className="flex items-center gap-2 bg-primary text-background px-6 py-3 font-heading font-bold text-xs uppercase tracking-wider hover:amber-glow transition-all whitespace-nowrap"
          >
            Ask Mike Directly
          </a>
        </div>
      </div>
    </section>
  );
}